import { useState, useEffect } from 'react';
import { Button, DatePicker, Radio, Select, Tooltip } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import dayjs from 'dayjs';
const { Option } = Select;

const { RangePicker } = DatePicker;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 16px;

  .ant-radio-group {
    display: flex;
    flex-wrap: nowrap;
    gap: 4px;
  }

  .ant-radio-button-wrapper {
    padding: 0 10px;
    white-space: nowrap;
  }
`;

// The arrows belong to the month select, so they sit in their own tight
// group instead of inheriting the Wrapper's 16px gap.
const MonthGroup = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 4px;
`;

// Year buttons are shortcuts for currentYear-1 … currentYear+1; the dropdown
// next to them reaches any year in MIN_YEAR … MAX_YEAR. Stepping months rolls
// over into the neighbouring year but stays inside the dropdown's range, so a
// year always stays selected in one of the two controls.
const YEAR_OFFSETS = [-1, 0, 1];
const MIN_YEAR = 2020;
const MAX_YEAR = 2030;
// "Vsa leta" clears the period entirely (date_range → null, i.e. no date
// filter). Sentinel rather than '' so it can't be mistaken for a year.
const ALL_YEARS_VALUE = 'all';
const YEAR_DROPDOWN_OPTIONS = [
  { label: 'Vsa leta', value: ALL_YEARS_VALUE },
  ...Array.from({ length: MAX_YEAR - MIN_YEAR + 1 }, (_, i) => {
    const y = (MIN_YEAR + i).toString();
    return { label: y, value: y };
  }),
];

// Buttons + dropdown read as one year control, so they sit tighter than the
// Wrapper's 16px gap (same idea as MonthGroup).
const YearGroup = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 4px;
`;

const parseRangeValue = (value) => {
  if (!value?.startsWith('range:')) return null;
  const parts = value.slice(6).split('-');
  if (parts.length !== 2) return null;
  return [dayjs(parts[0], 'DD.MM.YYYY'), dayjs(parts[1], 'DD.MM.YYYY')];
};

const formatRangeString = (dates) => {
  return `range:${dates[0].format('DD.MM.YYYY')}-${dates[1].format('DD.MM.YYYY')}`;
};

const DtRangeSelector = ({ value, onChange, disabled }) => {
  const currentYear = new Date().getFullYear();
  const [showRangePicker, setShowRangePicker] = useState(false);
  // Nothing preselected: the year comes from `value` (synced below).
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedCustom, setSelectedCustom] = useState(null);

  const isRange = value?.startsWith('range:');
  const rangeDates = isRange ? parseRangeValue(value) : null;

  const years = YEAR_OFFSETS.map((offset) => currentYear + offset);
  // A shortcut year is shown by its button; any other year by the dropdown.
  // With no period at all the dropdown says so ("Vsa leta"); a preset or a
  // custom range leaves it on the placeholder, since those aren't "all years".
  const isShortcutYear = years.map(String).includes(selectedYear);
  const yearDropdownValue = !value
    ? ALL_YEARS_VALUE
    : isShortcutYear ? null : selectedYear;

  // The month after/before the current selection, or null when there is no
  // month selected or the step would leave the dropdown's year range. A
  // selection that already sits outside that range (an old saved filter)
  // widens it rather than disabling both arrows.
  const steppedMonth = (delta) => {
    if (!selectedMonth) return null;
    const year = Number(selectedYear) || currentYear;
    const next = dayjs(`${year}-${selectedMonth}-01`).add(delta, 'month');
    const nextYear = next.year();
    if (nextYear < Math.min(MIN_YEAR, year) || nextYear > Math.max(MAX_YEAR, year)) return null;
    return { year: nextYear.toString(), month: next.format('MM') };
  };

  const stepMonth = (delta) => {
    const next = steppedMonth(delta);
    if (!next) return;
    setSelectedYear(next.year);
    setSelectedMonth(next.month);
    setSelectedCustom(null);
    setShowRangePicker(false);
    onChange?.(`ym-${next.year}-${next.month}`);
  };

  const handleYearSelect = (val) => {
    setSelectedYear(val);
    setSelectedCustom(null);
    setShowRangePicker(false);

    if (selectedMonth) {
      onChange?.(`ym-${val}-${selectedMonth}`);
    } else {
      onChange?.(val);
    }
  };

  // Drops the period filter altogether; the month goes with it, as it has no
  // year to belong to. Local state is reset by the `value` effect below.
  const handleYearClear = () => {
    onChange?.(null);
  };

  // Radio.Group only fires onChange on a *new* value, so a click on the year
  // that is already selected would do nothing; make it deselect instead.
  // Selecting another year is still left to onChange.
  const handleYearButtonClick = (year) => {
    if (year === selectedYear) handleYearClear();
  };

  const handleYearDropdownChange = (val) => {
    if (!val || val === ALL_YEARS_VALUE) {
      handleYearClear();
    } else {
      handleYearSelect(val);
    }
  };

  const handleMonthSelect = (val) => {
    const year = selectedYear || currentYear.toString();
    setSelectedYear(year);
    setSelectedMonth(val);
    setSelectedCustom(null);
    setShowRangePicker(false);
    console.log("handling month select", val)

    if (val == undefined){
      onChange?.(`${year}`);
    } else {
      onChange?.(`ym-${year}-${val}`);
    }

  };

  const handleCustomSelect = (val) => {
    setSelectedCustom(val);
    setSelectedMonth(null);
    setSelectedYear(null);
    setShowRangePicker(val === 'range');

    if (val !== 'range') {
      onChange?.(val);
    }
  };

  const handleRangeChange = (dates) => {
    if (dates?.length === 2) {
      const formatted = formatRangeString(dates);
      onChange?.(formatted);
    }
  };

  useEffect(() => {
    if (!value) {
      // Reset all local state when value is cleared
      setSelectedYear(null);
      setSelectedMonth(null);
      setSelectedCustom(null);
      setShowRangePicker(false);
      return;
    }

    // Handle range
    if (value.startsWith("range:")) {
      setSelectedYear(null);
      setSelectedMonth(null);
      setSelectedCustom("range");
      setShowRangePicker(true);
      return;
    }

    // Handle full year
    if (/^\d{4}$/.test(value)) {
      setSelectedYear(value);
      setSelectedMonth(null);
      setSelectedCustom(null);
      setShowRangePicker(false);
      return;
    }

    // Handle ym-YYYY-MM
    if (/^ym-\d{4}-\d{2}$/.test(value)) {
      const [_, y, m] = value.split("-");
      setSelectedYear(y);
      setSelectedMonth(m);
      setSelectedCustom(null);
      setShowRangePicker(false);
      return;
    }

    // Handle custom quick presets (-6m, -3m, etc.)
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedCustom(value);
    setShowRangePicker(value === "range");
  }, [value]);

  return (
    <Wrapper>
      <YearGroup>
        <Radio.Group
          size="small"
          buttonStyle="solid"
          value={selectedYear}
          onChange={(e) => handleYearSelect(e.target.value)}
          disabled={disabled}
        >
          {years.map((year) => (
            <Radio.Button
              key={year}
              value={year.toString()}
              onClick={() => handleYearButtonClick(year.toString())}
            >
              {year}
            </Radio.Button>
          ))}
        </Radio.Group>
        <Select
          size="small"
          placeholder="Leto"
          style={{ width: 100 }}
          value={yearDropdownValue}
          options={YEAR_DROPDOWN_OPTIONS}
          onChange={handleYearDropdownChange}
          allowClear={!!value}
          disabled={disabled}
        />
      </YearGroup>

      {/* Month select is only relevant when not picking a custom range.
          A custom range clears the year/month selection, so we swap it out
          for the range picker to keep everything on one line. */}
      {!showRangePicker && (
        <MonthGroup>
          {/* Stepping one month at a time is the common move, so the arrows
              show up as soon as there is a month to step from. */}
          {selectedMonth && (
            <Tooltip title="Prejšnji mesec">
              <Button
                size="small"
                icon={<LeftOutlined />}
                onClick={() => stepMonth(-1)}
                disabled={disabled || !steppedMonth(-1)}
              />
            </Tooltip>
          )}
          <Select
            size="small"
            value={selectedMonth}
            onChange={handleMonthSelect}
            style={{ width: 120 }}
            allowClear
            placeholder="Celo leto"
            disabled={disabled}
          >
            <Option value="">CELO LETO</Option>
            <Option value="01">JAN</Option>
            <Option value="02">FEB</Option>
            <Option value="03">MAR</Option>
            <Option value="04">APR</Option>
            <Option value="05">MAJ</Option>
            <Option value="06">JUN</Option>
            <Option value="07">JUL</Option>
            <Option value="08">AUG</Option>
            <Option value="09">SEP</Option>
            <Option value="10">OKT</Option>
            <Option value="11">NOV</Option>
            <Option value="12">DEC</Option>
          </Select>
          {selectedMonth && (
            <Tooltip title="Naslednji mesec">
              <Button
                size="small"
                icon={<RightOutlined />}
                onClick={() => stepMonth(1)}
                disabled={disabled || !steppedMonth(1)}
              />
            </Tooltip>
          )}
        </MonthGroup>
      )}

      <Select
        placeholder='Izberi obdobje'
        size="small"
        value={selectedCustom}
        onChange={(value) => handleCustomSelect(value)}
        style={{ width: 120 }}
        disabled={disabled}
        options={[
          { value: "ytd", label: "YTD" },
          { value: "-3m", label: "-3M" },
          { value: "M", label: "M" },
          { value: "-7d", label: "-7D" },
          // "< Danes" is no longer offered, but the dashboard's past-due link
          // and older saved filters still arrive with it; list it only then,
          // so the select shows its label instead of the raw value.
          ...(value === "past_not_today"
            ? [{ value: "past_not_today", label: "< Danes" }]
            : []),
          { value: "past_and_today", label: "<= Danes" },
          { value: "today", label: "Danes" },
          { value: "+7d", label: "+7D" },
          { value: "+3m", label: "+3M" },
          { value: "range", label: "Obdobje" },
        ]}
      />

      {showRangePicker && (
        <RangePicker
          value={rangeDates}
          onChange={handleRangeChange}
          allowClear={false}
          format="DD.MM.YYYY"
          size="small"
          style={{ minWidth: 240 }}
          disabled={disabled}
        />
      )}
    </Wrapper>
  );
};

export default DtRangeSelector;
